import type { ListClaimsInput } from "@amzn/ttechclaimintakeservice-client";

import { listClaimsService } from "src/api/claims/listClaims";

import type { ClaimRecord, ClaimType } from "./types";

/**
 * A retrieval request as the dispatcher assembles it. `queryName` selects the
 * named query within the claim type's manifest and `queryInput` carries its
 * inputs keyed by DynamoDB attribute name, so a new searchable field is a
 * config change rather than a new request shape.
 */
export interface RetrievalRequest {
  readonly claimType: ClaimType;
  readonly queryName: string;
  readonly queryInput: Readonly<Record<string, string | number | readonly string[]>>;
  readonly pageSize: number;
  readonly signal: AbortSignal;
}

export interface RetrievalOutcome {
  readonly rows: readonly ClaimRecord[];
  readonly hasMoreResults: boolean;
}

/**
 * The one retrieval call, for every line of business and every searchable
 * field, plus the normalization of the one response shape it returns.
 *
 * Rows are cast rather than mapped field by field: `ListClaimsOutput.claims` is
 * `Document[]`, and the attributes present are whatever the executed named
 * query lists in its `outputFields`, so there is no typed summary to map to.
 */
export async function retrieveClaims(request: RetrievalRequest): Promise<RetrievalOutcome> {
  const input: ListClaimsInput = {
    claimType: request.claimType,
    queryName: request.queryName,
    // A country filter travels as a `readonly string[]`, and the document type's
    // array member is mutable, so the assignment fails on the readonly modifier
    // alone even though every value here is document-safe.
    queryInput: request.queryInput as ListClaimsInput["queryInput"],
    pageSize: request.pageSize,
  };

  const response = await listClaimsService(input, request.signal);

  // A null response means no authenticated client could be built. Reporting it
  // as zero claims would present an authentication failure as an empty result
  // set, so it surfaces as a rejection the caller renders as a failure.
  if (!response) {
    throw new Error("Unable to list claims: no authenticated client is available.");
  }

  return {
    rows: (response.claims ?? []) as readonly ClaimRecord[],
    hasMoreResults: response.nextPageToken !== undefined && response.nextPageToken !== "",
  };
}
